import React, {useMemo, useState} from 'react';

import LoadingSpinner from "../../../components/LoadingSpinner";
import {Collapse} from "antd";
import ErrorBanner from "../../../components/ErrorBanner";
import {useOutletContext} from "react-router-dom";
import {usePositionForm} from "../../../providers/PositionFormProvider";
import PositionRoleTagSearch from "./PositionRoleTagSearch";
import {useAPI} from "../../../config/API";
import {useMount} from "react-use";

const MAX_ROLES = 5;


const PositionTemplateGrid = ({templates, onChange, onRemove, isDisabled, ...props}) => {
    const {formModel,position, onFormSubmit} = usePositionForm();

    const selectedTemplateIds = useMemo(() => TB.map(formModel.templates || [], 'id'), [formModel.templates]);

    return (
        <div className="grid grid-cols-3 md:grid-cols-3 sm:grid-cols-1 gap-4">
            {TB.map(templates, template => {
                const selected = selectedTemplateIds.includes(template.id);

                return (
                    <div key={`match-${template.id}`}
                         className="justify-self-stretch self-stretch flex flex-col items-stretch">
                        <div className={TB.mapClass("role-template-card", {selected})}>
                            <div className="flex flex-col gap-2">
                                <h6>{selected &&
                                    <i className="fas fa-check-circle mr-2"/>}{template.name}</h6>
                                <p>{template.description}</p>
                            </div>
                            <button
                                type="button"
                                disabled={isDisabled || (!selected && formModel.templates?.length >= 5)}
                                className={TB.mapClass("btn border", {selected})}
                                onClick={event => {
                                    event.currentTarget.blur()
                                    event.target.blur()

                                    return onChange(template)
                                }}>
                                {selected ? 'Unselect' : 'Select'}
                            </button>
                        </div>
                    </div>
                )
            })}
        </div>
    )
}
const PositionDisciplines = props => {

    const {formModel, position, onFormSubmit} = usePositionForm();
    const {
        isDisabled, goBack, onSubmit, onChange
    } = useOutletContext();

    const templateCategories = laravel.position_template_categories || [];
    const [isLoading, setIsLoading] = useState(false);

    const {
        data: bestMatches,
        isInitialLoading: isLoadingBestMatches,
        error: errorBestMatches,
        // refetch
    } = useAPI(['fetchTemplatesFromTags', formModel.tags],
         {
            enabled: formModel.tags?.length > 0,
            //
            // select: (templates) => {
            //     return templates
            // },
            // onSuccess: (suggest) => setSuggestions(suggest)
        }
    );

    useMount(() => {
        if (TB.isLocalEnv() && !position.id && !position.templates?.length) {
            let selectedTemplates = TB.sampleSize(TB.flatMap(templateCategories, 'templates'), 4);
            console.log('selectedTemplates', selectedTemplates);
            return onChange({templates: selectedTemplates})
        }
    })

    const selectedTemplateIds = TB.map(formModel.templates || [], 'id');

    const bestMatchesCount = useMemo(() => {
        const matchIds = TB.map(formModel.templates || [], 'id');
        return TB.filter(bestMatches, match => matchIds.includes(match.id)).length
    }, [formModel.templates, bestMatches]);


    const onToggleTemplate = async(template) => {
        let removedSelectedTemplate = TB.find(position.templates || [], ['id', template.id]);
        if (removedSelectedTemplate) {
            let updates = await position.removeTemplateUpdate(template);

            if (updates) {
                // Post updates if position exists in DB and is still valid
                if(TB.isDefined(position.id)
                    && !position.needsSetup()
                    && updates.disciplines?.length > 0
                    && updates.skills?.length > 0) {
                    return onFormSubmit(updates)
                }
                return onChange(updates)
            }

        }else {
            return onChange({templates: TB.xorBy(formModel.templates || [], [template],'id')})
        }
    }
    const templatesRemaining = MAX_ROLES - formModel.templates?.length;

    return (
        <div className="flex flex-col gap-3">

            <div className="flex flex-col justify-start items-start self-stretch relative gap-4">
                <p className="self-stretch text-lg font-semibold text-left text-black">
                    Let’s select up to five Wripple roles that are potential fits for this position.
                </p>
                {!formModel.templates?.length && (
                    <p className="self-stretch text-xs text-wrippleBlueGray">
                        No roles selected yet.
                    </p>
                )}
            </div>

            {formModel.templates?.length > 0 && (
                <div className="flex flex-col gap-[12px]">
                    <div className="flex flex-row flex-wrap gap-[16px]">

                        {TB.map(formModel.templates, template => (
                            <div key={`selected-${template.id}`}
                                 className="flex justify-start items-center relative gap-2 px-3 py-2 bg-[#5ae399]">

                                <p className="text-xs font-medium text-left text-[#3f434a]">
                                    {template.name}
                                </p>
                                <button
                                    type="button"
                                    disabled={isDisabled}
                                    onClick={(event) => {
                                        event.currentTarget.blur();
                                        event.target.blur();
                                        return onToggleTemplate(template)
                                    }}
                                    className="text-sm text-left text-[#3f434a]">
                                    <i className="fas fa-times"/>
                                </button>
                            </div>
                        ))}

                    </div>

                    <p className="self-stretch text-xs text-wrippleBlueGray">
                        {!templatesRemaining ? (
                            'Max roles selected'
                        ) : (
                            `${templatesRemaining} more ${TB.pluralize("role", templatesRemaining)} can be added`
                        )}
                    </p>
                </div>
            )}

            <hr />

            <div className="flex flex-col gap-4">
                <p className="text-sm font-medium text-left text-black">
                    <i className="fa fa-arrow-right text-primary mr-2" /> Search for roles
                </p>

                <PositionRoleTagSearch {...({isDisabled, goBack, onSubmit, onChange })}/>
            </div>

            {formModel?.tags?.length > 0 && (
                <Collapse defaultActiveKey={['match']} className="role-templates-collapse">
                    <Collapse.Panel key={'match'} header={(
                        <div className="role-template-header">
                            <h6>Best Matches</h6>
                            {!isLoadingBestMatches && (
                                <p>{TB.pluralize('role', bestMatches.length, true)}</p>
                            )}
                            {bestMatchesCount > 0 && (
                                <div className="category-match">
                                    {bestMatchesCount} selected
                                </div>
                            )}
                        </div>
                    )}>
                        {isLoadingBestMatches && (
                            <div className="flex flex-col items-center justify-center min-h-[200px] gap-2.5">
                                <p>Finding Best Matches</p>
                                <LoadingSpinner size="lg"/>
                            </div>
                        )}
                        {errorBestMatches && (
                            <div className="flex flex-col items-center justify-center min-h-[200px]">
                                <ErrorBanner message="error"/>
                            </div>
                        )}
                        {bestMatches?.length > 0 && (
                            <PositionTemplateGrid
                                templates={bestMatches}
                                onChange={onToggleTemplate}
                                isDisabled={isDisabled}
                            />
                        )}

                    </Collapse.Panel>
                </Collapse>
            )}

            <hr />

            <div className="flex flex-col justify-start items-stretch self-stretch relative gap-4">
                <p className="text-sm font-medium text-left text-black">
                    <i className="fa fa-arrow-right text-primary mr-2" /> Select roles by category
                </p>

                {isLoading && (
                    <div className="flex flex-col items-center justify-center min-h-[300px]">
                        <LoadingSpinner size="lg"/>
                    </div>
                )}

                {!isLoading && templateCategories?.length > 0 && (
                    <Collapse accordion className="role-templates-collapse">

                        {TB.map(templateCategories, category => {
                            if (!category.templates?.length) {
                                return null;
                            }
                            const matchCount = TB.filter(category.templates, template => selectedTemplateIds.includes(template.id)).length
                            return (
                                <Collapse.Panel key={category.id} header={(
                                    <div className="role-template-header">
                                        <h6>{category.name}</h6>

                                        <p>{TB.pluralize('role', category.templates?.length, true)}</p>

                                        {matchCount > 0 && (
                                            <div className="category-match">
                                                {matchCount} selected
                                            </div>
                                        )}
                                    </div>
                                )}>
                                    <PositionTemplateGrid
                                        templates={category.templates}
                                        onChange={onToggleTemplate}
                                        isDisabled={isDisabled}
                                    />
                                </Collapse.Panel>
                            )
                        })}
                    </Collapse>
                )}
            </div>
        </div>
    );
};

PositionDisciplines.propTypes = {};

export default PositionDisciplines;